import { useEffect, useState } from 'react';
import { AuthContext } from './auth-context';
import { getStudentProfile, loginStudent, logoutStudent } from '../lib/odooStudentAuth';

const TOKEN_KEY = 'student_portal_odoo_token';

function toPortalUser(student) {
  const name = String(student.name || '').trim();
  const parts = name.split(/\s+/);
  return {
    id: String(student.id || student.user_id || student.student_no),
    studentId: student.student_no,
    firstName: student.first_name || parts[0] || 'Student',
    lastName: student.last_name || parts.slice(1).join(' '),
    email: student.email || '',
    faculty: student.faculty || '',
    department: student.department || '',
    batch: student.batch || '',
    role: 'student',
  };
}

function profileToStudent(profile) {
  return {
    ...profile.personal_info,
    ...profile.academic_info,
    id: profile.academic_info.student_no,
    name: profile.personal_info.name,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return undefined;
    }
    getStudentProfile(token)
      .then((profile) => {
        if (active) setUser(toPortalUser(profileToStudent(profile)));
      })
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const login = async (studentId, password) => {
    const result = await loginStudent(studentId, password);
    sessionStorage.setItem(TOKEN_KEY, result.token);
    setUser(toPortalUser(result.student));
  };

  const logout = () => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
    logoutStudent(token).catch(() => {});
  };

  const refreshUser = async () => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const profile = await getStudentProfile(token);
    setUser(toPortalUser(profileToStudent(profile)));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token: sessionStorage.getItem(TOKEN_KEY),
      loading,
      login,
      logout,
      isAuthenticated: Boolean(user),
      refreshUser,
      isDashboardPreview: false,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
